import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface InvoiceMapping {
  invoiceNo: string;
  bodies: string[];
}

interface ManageChoicesModalProps {
  isOpen: boolean;
  onClose: () => void;
  mappings: InvoiceMapping[];
  cases: string[];
  boxes: string[];
  onSave: (mappings: InvoiceMapping[], cases: string[], boxes: string[]) => void;
}

export function ManageChoicesModal({ isOpen, onClose, mappings, cases, boxes, onSave }: ManageChoicesModalProps) {
  const [localMappings, setLocalMappings] = useState<InvoiceMapping[]>([]);
  const [localCases, setLocalCases] = useState('');
  const [localBoxes, setLocalBoxes] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLocalMappings(JSON.parse(JSON.stringify(mappings)));
      setLocalCases(cases.join(', '));
      setLocalBoxes(boxes.join(', '));
    }
  }, [isOpen, mappings, cases, boxes]);

  const updateMapping = (idx: number, field: 'invoiceNo' | 'bodies', value: any) => {
    setLocalMappings((prev) => {
      const next = [...prev];
      if (field === 'bodies') {
        next[idx] = { ...next[idx], bodies: value };
      } else {
        next[idx] = { ...next[idx], [field]: value };
      }
      return next;
    });
  };

  const addGroup = () => {
    setLocalMappings((prev) => [...prev, { invoiceNo: '', bodies: [] }]);
  };

  const removeGroup = (idx: number) => {
    setLocalMappings((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    const parseList = (str: string) => str.split(/[\n,]+/).map((s) => s.trim()).filter((s) => s.length > 0);
    onSave(
      localMappings.filter((m) => m.invoiceNo.trim()),
      parseList(localCases),
      parseList(localBoxes)
    );
    onClose();
  };

  return (
    <Modal id="manage-choices" isOpen={isOpen} onClose={onClose} title="Manage Invoice-Body Links & Choice Options" size="lg">
      <div className="space-y-5">
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold">Invoice-Body Mappings</h3>
            <Button variant="secondary" size="sm" onClick={addGroup}>+ Add Group</Button>
          </div>
          {localMappings.map((group, idx) => (
            <div key={idx} className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-xl p-4 mb-3">
              <div className="flex items-center justify-between mb-3">
                <strong className="text-accent font-mono text-sm">Group #{idx + 1}</strong>
                <button
                  className="text-xs font-bold text-[var(--status-ng-text)] hover:underline"
                  onClick={() => removeGroup(idx)}
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="form-group">
                  <label className="form-label">Invoice Number</label>
                  <input
                    type="text"
                    className="input-field font-mono"
                    placeholder="e.g. 408"
                    value={group.invoiceNo}
                    onChange={(e) => updateMapping(idx, 'invoiceNo', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Body Numbers (comma-separated)</label>
                  <textarea
                    className="input-field font-mono"
                    rows={2}
                    placeholder="e.g. TY273393, TY273394, TY273395"
                    value={group.bodies.join(', ')}
                    onChange={(e) => updateMapping(idx, 'bodies', e.target.value.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean))}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-[var(--border-color)] pt-4">
          <h3 className="text-base font-bold mb-3">Standalone Case / Box Choices</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">Case Numbers (comma-separated)</label>
              <input
                type="text"
                className="input-field font-mono"
                placeholder="e.g. 10, 11, 12, 13"
                value={localCases}
                onChange={(e) => setLocalCases(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Box Numbers (comma-separated)</label>
              <input
                type="text"
                className="input-field font-mono"
                placeholder="e.g. 408, 409, 410"
                value={localBoxes}
                onChange={(e) => setLocalBoxes(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border-color)]">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>Save & Apply Choice Options</Button>
        </div>
      </div>
    </Modal>
  );
}
